import React, { Dispatch, ReactElement, SetStateAction } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";

import { Profile, TemplateSet } from "../../services/api";

const TemplateSetForm: React.FC<{
  actionButtons?: ReactElement;
  isUpdate: boolean;
  templateSet: TemplateSet;
  setTemplateSet: Dispatch<SetStateAction<TemplateSet>>;
  profiles: Profile[];
}> = ({ actionButtons, isUpdate, templateSet, setTemplateSet, profiles }) => {
  const handleTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setTemplateSet(new TemplateSet({ ...templateSet!, [name]: value }));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTemplateSet((prevTemplateSet) => {
      const newTemplateNames = new Set(prevTemplateSet.template_names);
      if (event.target.checked) {
        newTemplateNames.add(event.target.name);
      } else {
        newTemplateNames.delete(event.target.name);
      }

      return new TemplateSet({
        ...prevTemplateSet!,
        template_names: Array.from(newTemplateNames),
      });
    });
  };

  const renderMappedTemplates = () => {
    const err = templateSet.hasError("template_names");

    return (
      <FormControl sx={{ m: 3 }} component="fieldset" variant="standard" required error={!!err}>
        <FormLabel component="legend">mapped templates</FormLabel>
        <FormGroup>
          {profiles.map(({ profile }) => (
            <FormControlLabel
              key={`profile-${profile}`}
              control={
                <Checkbox
                  checked={!!templateSet.template_names?.find((v) => v === profile)}
                  name={profile}
                  onChange={handleCheckboxChange}
                />
              }
              label={profile}
            />
          ))}
        </FormGroup>
        <FormHelperText data-testid="error-text-mapped_templates" error={!!err}>
          {err ?? null}
        </FormHelperText>
      </FormControl>
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid item sx={{ marginTop: "15pt" }} md={4} xs={12}>
        <Typography sx={{ fontSize: "20pt", wordBreak: "break-word" }}>
          {isUpdate ? templateSet?.id : "Create Set"}
        </Typography>
      </Grid>
      <Grid item md={true}>
        <Box
          sx={{
            "& > :not(style)": { m: 1, width: "100%" },
          }}
          component="form"
          autoComplete="off"
        >
          {Object.entries(templateSet.fieldsWithType()).map(([key, type]) => {
            if (key === "template_names") return null;
            if (key === "disabled") return null;
            if (isUpdate && key === "id") return null;

            const error = templateSet.hasError(key);
            return (
              <TextField
                required
                name={key}
                key={key}
                type={type}
                label={key.replace(/_/g, " ")}
                variant="standard"
                autoComplete="off"
                error={!!error}
                helperText={error}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {error ? (
                        <ErrorIcon sx={{ color: "red" }} data-testid={`error-icon-${key}`} />
                      ) : (
                        <CheckCircleIcon sx={{ color: "green" }} data-testid={`valid-icon-${key}`} />
                      )}
                    </InputAdornment>
                  ),
                }}
                value={templateSet[key as keyof TemplateSet] ?? ""}
                onChange={handleTextInputChange}
              />
            );
          })}
          {renderMappedTemplates()}
        </Box>
        {actionButtons}
      </Grid>
    </Grid>
  );
};

export default TemplateSetForm;
